// import * as R from 'ramda';
import {
  takeLatest,
  takeEvery,
  select,
  // spawn,
  put,
} from 'redux-saga/effects';
import {
  generativeAi as genAiApis,
  ti2 as ti2Apis,
  aiRules as aiRulesApis,
} from '../../../api.js';
import { apiSaga, apiSagaJob } from '../../../root-sagas.js';
import { _getAccountInfo } from './partner-settings/edit-app-key-configuration-sagas.js';
import * as R from 'ramda';

function* onLocationChange({ payload }) {
  const pathname = R.pathOr('', ['location', 'pathname'], payload);
  if (pathname.includes('generative-ai') && pathname.includes('status-check')) {
    const { companyId, ti2token } = yield _getAccountInfo();
    const partners = yield select(R.pathOr([], ['PM', 'partnerList', 'list', 'partners']));
    if (partners.length === 0) {
      yield put({ type: '@partner-list/GET_PARTNERS' })
    }
    yield apiSaga('genAi_statusCheck_loadAllAppKeys', ti2Apis.getAllAppKeys, [{ companyId, ti2token }])
  }
}

/**
 * Triggers a status check for the generative AI.
 *
 * @param {object} payload - The payload containing the query text and all app keys.
 * @param {object[]} [payload.integrations] - An array of integration objects with hint and integrationId properties.
 * @param {string} payload.integrations[].hint - partnerId or 'Direct Booking'
 * @param {string} payload.integrations[].integrationId - 'tourplan', 'fareharbor', etc.
 * @yields {object} The result of the API call.
 */

function* getStatusCheck({ payload }) {
  const { companyId, ti2token } = yield _getAccountInfo();
  const { queryText, allAppKeys } = yield select(state => state.generativeAi.statusCheck);
  
  const params = {
    companyId,
    ti2token,
    integrations: R.path(['integrations', 'length'], payload) ? payload.integrations : allAppKeys,
    queryText,
  };
  yield apiSagaJob('genAi_statusCheck', genAiApis.vectordbStatusCheck, [params, { skipLoader: true }]);
}

function* testProductMatch({ payload }) {
  const { companyId, ti2token } = yield _getAccountInfo();
  const params = {
    ti2: {
      url: process.env.REACT_APP_TI2_URL,
      token: ti2token,
      app: payload.integrationId,
      hint: payload.hint,
    },
    testMatcher: payload.queryText,
    scopes: [payload.hint],
    companyId,
    findProducts: true,
  };
  yield apiSagaJob('genAi_testProductMatch', aiRulesApis.productRuleEval, [params, { skipLoader: true }]);
}

function* refreshStores({ payload }) {
  yield apiSagaJob('genAi_refreshStores', genAiApis.refreshStores, [{
    partnerId: payload.partnerId,
    reason: 'TourConnect app vector store rebuild',
  }, { skipLoader: true }]);
}

const getForceRefreshTargets = (allAppKeys, payload) => {
  const hint = payload.hint || payload.partnerId;
  const payloadIntegrations = R.pathOr([], ['integrations'], payload);
  const integrations = payloadIntegrations.length ? payloadIntegrations : allAppKeys;
  const targets = integrations.filter(app => {
    if (app.hint !== hint) return false;
    return !payload.integrationId || app.integrationId === payload.integrationId;
  });

  if (!targets.length && hint && payload.integrationId) {
    return { hint, targets: [{ hint, integrationId: payload.integrationId }] };
  }

  return {
    hint,
    targets: targets.map(({ hint, integrationId }) => ({ hint, integrationId })),
  };
};

export function* forceRefreshTi2Products({ payload = {} }) {
  const { allAppKeys } = yield select(state => state.generativeAi.statusCheck);
  const { hint, targets } = getForceRefreshTargets(allAppKeys, payload);
  const args = [{ hint, integrations: targets }];

  if (!hint || !targets.length) {
    yield put({
      type: 'genAi_forceRefreshTi2Products_ERROR',
      payload: 'No Ti2 integration found for this partner.',
      args,
    });
    return;
  }

  yield put({ type: 'genAi_forceRefreshTi2Products_STARTED', args });
  const { companyId, ti2token } = yield _getAccountInfo();
  const refreshedIntegrations = [];
  const failedIntegrations = [];

  for (const target of targets) {
    const response = yield apiSaga('genAi_forceRefreshTi2Products_refreshIntegration', ti2Apis.forceRefreshProducts, [{
      companyId,
      ti2token,
      app: target.integrationId,
      hint: target.hint,
    }]);

    if (response) {
      refreshedIntegrations.push(target);
    } else {
      failedIntegrations.push(target);
    }
  }

  if (refreshedIntegrations.length) {
    yield put({ type: 'genAi_forceRefreshTi2Products_SUCCESS', args: [{ hint, integrations: refreshedIntegrations }] });
    yield put({ type: 'genAi_statusCheck', payload: { integrations: refreshedIntegrations } });
  }

  if (failedIntegrations.length) {
    yield put({
      type: 'genAi_forceRefreshTi2Products_ERROR',
      payload: 'Unable to force refresh one or more Ti2 integrations.',
      args: [{ hint, integrations: failedIntegrations }],
    });
  }
}

/**
 * Handles the success of the refresh stores action.
 * 
 * @param {{ args: object, payload: object[] }} action - The action object containing the payload.
 * @param {object} action.args - The arguments passed to the original job action.
 * @param {object[]} action.payload - An array of objects containing the job status information.
 * @param {string} action.payload[0].jobId - The ID of the job.
 * @param {string} action.payload[0].status - The status of the job.
 * @param {string} action.payload[0].result - The result of the job.
 * @param {string} action.payload[0].log - The log information of the job.
 */
function* onRefreshStoresSuccess({ args, jobResults }) {
  console.log('onRefreshStoresSuccess', args, jobResults);
  if (R.path([0, 'status'], jobResults) === 'success') {
    console.log('hit hereeee')
    const { allAppKeys } = yield select(state => state.generativeAi.statusCheck);
    const partnerId = R.pathOr(null, [0, 'partnerId'], args);
    // if partnerId is null, we had refreshed stores for all partners hence we need to re-run the status check for all app keys
    const integration = partnerId ? allAppKeys.find(app => app.hint === partnerId) : allAppKeys;
    yield put({
      type: 'genAi_statusCheck',
      payload: integration ? { integrations: [integration] } : {},
    });
  }
}

function* onProductMatchSuccess({ args }) {
  const hint = R.pathOr('', [0, 'scopes', 0], args);
  const integrationId = R.pathOr('', [0, 'ti2', 'app'], args);
  yield put({ type: 'genAi_statusCheck', payload: { integrations: [{ hint, integrationId }] } });
}

// eslint-disable-next-line import/no-anonymous-default-export
export default [
  takeLatest('@@router/LOCATION_CHANGE', onLocationChange),
  takeLatest([
    'genAi_statusCheck',
    // IMPORTANT: this is too heavy to run for all integrations
    // we will just run it for any partner we are trying to investigate
    // keep in mind that this feature (status check page) is only used by TC admins
    // 'genAi_statusCheck_loadAllAppKeys_SUCCESS',
  ], getStatusCheck),
  takeLatest('genAi_refreshStores_SUCCESS', onRefreshStoresSuccess),
  takeLatest('genAi_testProductMatch', testProductMatch),
  takeLatest('genAi_testProductMatch_SUCCESS', onProductMatchSuccess),
  takeLatest('genAi_refreshStores', refreshStores),
  takeEvery('genAi_forceRefreshTi2Products', forceRefreshTi2Products),
];
